import { useCallback, useEffect, useState } from 'react';

import { useCurrentUser } from '@bonusly/who-am-i';

const STORAGE_KEY = 'bonusly:recent-spoof-users';
const MAX_RECENT = 6;

export interface RecentSpoofUser {
	id: string;
	email: string;
	displayName: string;
}

const readFromStorage = (): RecentSpoofUser[] => {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		return raw ? JSON.parse(raw) : [];
	} catch {
		return [];
	}
};

const writeToStorage = (users: RecentSpoofUser[]) => {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(users));
	} catch {
		// localStorage may be full or unavailable
	}
};

const addUser = (user: RecentSpoofUser): RecentSpoofUser[] => {
	const existing = readFromStorage().filter((u) => u.id !== user.id);
	const updated = [user, ...existing].slice(0, MAX_RECENT);
	writeToStorage(updated);
	return updated;
};

export const useRecentSpoofUsers = () => {
	const currentUser = useCurrentUser();
	const [recentUsers, setRecentUsers] = useState<RecentSpoofUser[]>(readFromStorage);

	useEffect(() => {
		if (!currentUser?.id) return;

		const updated = addUser({
			id: currentUser.id,
			email: currentUser.email,
			displayName: currentUser.display_name,
		});
		setRecentUsers(updated);
	}, [currentUser?.id, currentUser?.email, currentUser?.display_name]);

	const otherUsers = recentUsers.filter((u) => u.id !== currentUser?.id);

	const clearRecent = useCallback(() => {
		writeToStorage([]);
		setRecentUsers([]);
	}, []);

	return { recentUsers: otherUsers, clearRecent };
};
